import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
  } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { baseApi } from "../redux/baseAPI";
import baseApiHelper from "../services/baseApiHelper";
import { aclApi } from "../services/aclService";
import { useGetChatMetaQuery } from "../services/chatService";
import { getNurseUrgentMsg, isPatientChatMessage, NOTIFICATION_CLICK_MESSAGE_TYPE, getStoredNotificationPayload } from "../helpers/utility";
import { OAKMED_PRACTICE_ID } from "../utils/practiceIds";


const GroupChatContext = createContext();

const readInitialAuth = () => {
  try {
    if (window.location.pathname === "/provider") {
      const providerData = new URLSearchParams(window.location.search).get("data");
      if (providerData) return JSON.parse(providerData);
    }
    return JSON.parse(localStorage.getItem('auth') || "{}");
  } catch {
    return {};
  }
};
  
export function useGroupChatContext() {
    return useContext(GroupChatContext);
}
  
export function GroupChatProvider({ children }) {

  const defaultMsgObj = {data: [], page: 1, meta: {}}
  const dispatch = useDispatch();

  const filters = JSON.parse(localStorage.getItem('chatFilter') || null);
  const [authData, setAuthData] = useState(readInitialAuth);
  const [isOpeningSideBar, setIsOpeningSideBar] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});
  const [problemsMeta, setProblemsMeta] = useState({});
  const [isOpenPatientMedsAllergyPopup, setIsOpenPatientMedsAllergyPopup] = useState(false);
  const [isOpenRequestAppointmentPopup, setIsOpenRequestAppointmentPopup] = useState(false);
  const [openAppt, setOpenAppt] = useState({isOpen: false});
  const [isOpenPatientApptPopup, setIsOpenPatientApptPopup] = useState(false);
  const [searchedMessagethread, setSearchedMessageThread] = useState({thread_id: '', data:[]});
  const [selectedLocations, setSelectedLocations] = useState((filters?.selected_location || [])?.map(item => item.id)?.join(",") || "");
  const [chatFilter, setChatFilter] = useState(filters || { unread_only: false, urgent_only: false, unacknowledged: false, mention: false, attachment: false, selected_location: [] });
  const [messages, setMessages] = useState(defaultMsgObj);
  const pendingMessagesRef = useRef({});
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isStopwatchActive, setIsStopwatchActive] = useState(false);
  const [isOpenStopWatchPopup, setIsOpenStopWatchPopup] = useState(false);
  const [elapsedTime, setElapsedTime] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [vezoCallData, setVezoCallData] = useState(null);
  const defaultProviderUrgentMsg = "For urgent assistance , please call the facility staff directly via phone.";
  const [providerUrgentMsg, setProviderUrgentMsg] = useState(defaultProviderUrgentMsg);
  const [nurseUrgentMsg, setNurseUrgentMsg] = useState(getNurseUrgentMsg);
  const[isShowPinCreateScreenOnFirstLogin, setIsShowPinCreateScreenOnFirstLogin] = useState(JSON.parse(localStorage.getItem('createPinOnFirstLogin')) == null? false: localStorage.getItem('createPinOnFirstLogin'));
  const [isPinCodeScreenActive, setIsPinCodeScreenActive] = useState(JSON.parse(localStorage.getItem('isPinCodeScreenActive')) == null? false: localStorage.getItem('isPinCodeScreenActive'));
  const [selectedMessageToReply, setSelectedMessageToReply] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [callData, setCallData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showGlobalDocuments, setShowGlobalDocuments] = useState(false);
  const [isOpenChangePinCodePopup, setIsOpenChangePinCodePopup] = useState(false);
  const [isPrintMessage, setIsPrintMessage] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectedChatTab, setSelectedChatTab] = useState('patient_chats');
  const [isForceOpen2FaPopup, setForceOpen2FaPopup] = useState(false);
  const [isUrgentMessage, setIsUrgentMessage] = useState(0);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const readStoredUserAcl = () => {
    try {
      const raw = localStorage.getItem("user_acl");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const [userAcl, setUserAclState] = useState(readStoredUserAcl);

  const { data: chatMeta } = useGetChatMetaQuery(undefined, { skip: !authData?.auth });
  const isOakmedPractice = `${authData?.practice?.id || authData?.practice_id}` === OAKMED_PRACTICE_ID;
  const urgentAckMessageEnabled = chatMeta?.nursing_chat_show_urgent_ack_message;
  const shouldShowUrgentAckMessage = !isOakmedPractice || urgentAckMessageEnabled === 1 || urgentAckMessageEnabled === "1" || urgentAckMessageEnabled === true;

  useEffect(() => {
    const disclaimerMessage = chatMeta?.facility_chat_disclaimer_message?.trim();
    setProviderUrgentMsg(disclaimerMessage || defaultProviderUrgentMsg);
    setNurseUrgentMsg(disclaimerMessage || getNurseUrgentMsg());
  }, [chatMeta?.facility_chat_disclaimer_message]);

  const setUserAcl = useCallback((payload) => {
    if (payload == null || payload === undefined) {
      localStorage.removeItem("user_acl");
      setUserAclState(null);
      return;
    }
    localStorage.setItem("user_acl", JSON.stringify(payload));
    setUserAclState(payload);
  }, []);

  const refreshUserAcl = useCallback(async () => {
    let authParsed = {};
    try {
      authParsed = JSON.parse(localStorage.getItem("auth") || "{}");
    } catch {
      authParsed = {};
    }
    if (!authParsed?.is_provider) {
      setUserAcl(null);
      return null;
    }
    try {
      const result = await dispatch(
        aclApi.endpoints.getUserPermissions.initiate(undefined, { forceRefetch: true })
      );
      if (result.error) {
        setUserAcl(null);
        return null;
      }
      const parsed = result.data;
      if (parsed == null) {
        setUserAcl(null);
        return null;
      }
      const ok = parsed.code === 0 || parsed.code === "0";
      if (ok) {
        const next = parsed.data !== undefined && parsed.data !== null ? parsed.data : {};
        setUserAcl(next);
        return next;
      }
      setUserAcl(null);
      return null;
    } catch {
      setUserAcl(null);
      return null;
    }
  }, [dispatch, setUserAcl]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!authData?.id) {
      setUserAcl(null);
      return;
    }
    if (!authData?.is_provider) {
      setUserAcl(null);
      localStorage.removeItem("user_acl");
      return;
    }
    refreshUserAcl();
  }, [authData?.id, authData?.is_provider, refreshUserAcl, setUserAcl]);

  const logoutApp = () => {
    localStorage.clear();
    setIsPinCodeScreenActive(false);
    dispatch(baseApi.util.resetApiState());
    setAuthData({});
    setSelectedChat({});
    setSelectedLocations('');
    setForceOpen2FaPopup(false);
    setUserAclState(null);
    navigate('/auth/login')
  }

  const getChatKey = useCallback((chat) => {
    return isPatientChatMessage(chat) ? `patient_${chat.pid}` : `location_${chat.location_id || chat.location?.id}`;
  }, []);

  const addPendingMessage = useCallback((chat, message) => {
    const chatKey = getChatKey(chat);
    if (!pendingMessagesRef.current[chatKey]) {
      pendingMessagesRef.current[chatKey] = [];
    }
    pendingMessagesRef.current[chatKey].push(message);
  }, [getChatKey]);

  const removePendingMessage = useCallback((chat, tempMessageId) => {
    const chatKey = getChatKey(chat);
    if (pendingMessagesRef.current[chatKey]) {
      pendingMessagesRef.current[chatKey] = pendingMessagesRef.current[chatKey].filter(
        msg => msg.temp_message_id !== tempMessageId
      );
    }
  }, [getChatKey]);

  const getPendingMessages = useCallback((chat) => {
    const chatKey = getChatKey(chat);
    return pendingMessagesRef.current[chatKey] || [];
  }, [getChatKey]);

  const loadThreadChats = useCallback((chat) => {

    const isPatientChat = isPatientChatMessage(chat);
    const clearChatUnreadCounts = (chatToClear) => {
      if(!chatToClear?.pid && !(chatToClear?.location_id || chatToClear?.location?.id)){
        return;
      }
      if(isPatientChatMessage(chatToClear)){
        baseApiHelper().updatePatientChatCacheThroughPusher({
          pid: chatToClear.pid,
          location_id: chatToClear.location_id || chatToClear.location?.id,
          location: chatToClear.location,
          patient: chatToClear.patient,
          unread_message_count: 0,
          unread_urgent_count: 0,
          unread_mention_count: 0
        }, selectedLocations);
      } else {
        baseApiHelper().updateLocationChatCacheThroughPusher({
          location_id: chatToClear.location_id || chatToClear.location?.id,
          location: chatToClear.location,
          unread_message_count: 0,
          unread_urgent_count: 0,
          unread_mention_count: 0
        }, selectedLocations);
      }
    };
    const chatToOpen = {
      ...chat,
      _openedAt: Date.now(),
      _openedUnreadMessageCount: Number(chat?.unread_message_count || 0),
      _openedLastRead: chat?.last_read,
    };
    clearChatUnreadCounts(selectedChat);
    clearChatUnreadCounts(chat);

    const selectedChatLocationId = selectedChat.location_id || selectedChat.location?.id;
    const nextChatLocationId = chat.location_id || chat.location?.id;
    if((isPatientChat && (selectedChat.pid != chat.pid || selectedChatLocationId != nextChatLocationId)) || (!isPatientChat && selectedChatLocationId != nextChatLocationId)){
      setMessages({data: [], page: 1, meta: {}});
    }
    setSelectedChat(chatToOpen);
  }, [selectedChat, selectedLocations])

  const openChatFromNotification = useCallback((payload) => {
    const navPayload = getStoredNotificationPayload(payload);
    if (!navPayload?.chatType) return;

    setIsOpeningSideBar(false);

    if (navPayload.chatType === 'patient_chat' && navPayload.pid) {
      setSelectedChatTab('patient_chats');
      loadThreadChats({
        pid: navPayload.pid,
        patient: navPayload.patient,
        location: navPayload.location,
        isChatThreadClicked: true,
      });
      return;
    }

    if (navPayload.chatType === 'location_chat' && (navPayload.location_id || navPayload.location?.id)) {
      setSelectedChatTab('location_chats');
      loadThreadChats({
        location_id: navPayload.location_id || navPayload.location?.id,
        location: navPayload.location,
        isChatThreadClicked: true,
      });
    }
  }, [loadThreadChats]);

  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type !== NOTIFICATION_CLICK_MESSAGE_TYPE) return;
      openChatFromNotification(event.data.payload);
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [openChatFromNotification]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openChat = params.get('open_chat');
    const pid = params.get('pid');
    const locationId = params.get('location_id');

    if (!openChat) return;

    window.history.replaceState({}, '', window.location.pathname);

    if (openChat === 'patient' && pid) {
      openChatFromNotification({ chatType: 'patient_chat', pid });
    } else if (openChat === 'location' && locationId) {
      openChatFromNotification({ chatType: 'location_chat', location_id: locationId });
    }
  }, [openChatFromNotification]);
  
  const value = {
    authData,
    setAuthData,
    isOpeningSideBar,
    setIsOpeningSideBar,
    selectedChat,
    setSelectedChat,
    problemsMeta,
    setProblemsMeta,
    messages,
    setMessages,
    screenWidth,
    setScreenWidth,
    selectedLocations,
    setSelectedLocations,
    chatFilter,
    setChatFilter,
    providerUrgentMsg,
    nurseUrgentMsg,
    shouldShowUrgentAckMessage,
    isStopwatchActive,
    setIsStopwatchActive,
    isOpenStopWatchPopup,
    setIsOpenStopWatchPopup,
    elapsedTime,
    setElapsedTime,
    searchedMessagethread,
    setSearchedMessageThread,
    vezoCallData,
    setVezoCallData,
    isShowPinCreateScreenOnFirstLogin,
    setIsShowPinCreateScreenOnFirstLogin,
    isPinCodeScreenActive,
    setIsPinCodeScreenActive,
    isOpenRequestAppointmentPopup,
    openAppt,
    setOpenAppt,
    setHighlightedMessageId,
    highlightedMessageId,
    callData,
    setCallData,
    setShowSettings,
    setShowGlobalDocuments,
    showGlobalDocuments,
    setIsOpenPatientMedsAllergyPopup,
    isOpenPatientMedsAllergyPopup,
    setIsOpenRequestAppointmentPopup,
    logoutApp: logoutApp,
    loadThreadChats: loadThreadChats,
    showSettings,
    isOpenPatientApptPopup,
    setIsOpenPatientApptPopup,
    selectedMessageToReply,
    setSelectedMessageToReply,
    isOpenChangePinCodePopup,
    setIsOpenChangePinCodePopup,
    isPrintMessage,
    setIsPrintMessage,
    selectedMessages,
    setSelectedMessages,
    selectedChatTab,
    setSelectedChatTab,
    isForceOpen2FaPopup,
    setForceOpen2FaPopup,
    isUrgentMessage, 
    setIsUrgentMessage,
    addPendingMessage,
    removePendingMessage,
    getPendingMessages,
    setOpenDropdownId,
    openDropdownId,
    userAcl,
    refreshUserAcl,
  };
  
  return (
    <GroupChatContext.Provider value={value}>
      {children}
    </GroupChatContext.Provider>
  );
}
  